import { useEffect, useRef, useState } from 'react';

import { create as createSelector } from '@spazious/dollhouse-selector';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';

import { useLayers } from '../../hooks/index.js';
import { FCompositionNodeImpl } from '../../nodes/FCompositionNode.js';
import { FCompositionNode } from '../../types/index.js';
import { FGroup } from '../FGroup/FGroup.js';
import { Furniture } from '../Furniture/index.js';

import { useFCompositionInstance } from './useFCompositionInstance.js';

const defaultAttrs = {};

export type FCompositionProps = Omit<
  GraphNodeProps<FCompositionNode> & {
    attrs?: {
      [key: string]: any;
    };
  },
  'instance'
>;

/**
 * FComposition component is used to render a composition of furniture models.
 */
export const FComposition: React.FC<FCompositionProps> = ({
  id,
  attrs,
  selector,
  ...restProps
}) => {
  const layers = useLayers();
  const nodeRef = useRef<FCompositionNodeImpl>(null);

  const instance = useFCompositionInstance(id, attrs ?? defaultAttrs);

  // TODO to memoize the nodes we need to change how we sets the objects inside createHookInstance
  const [nodes, setNodes] = useState<
    Record<
      string,
      React.ReactElement<{
        selector: string;
      }>[]
    >
  >({});

  useEffect(() => {
    if (nodeRef.current) {
      setNodes(
        instance.getNodes().reduce(
          (acc, { id, position, rotation, type }, idx) => ({
            ...acc,
            [type]: [
              ...(acc[type] || []),
              <Furniture
                key={idx}
                id={id}
                originator={nodeRef.current}
                position={position}
                rotation={rotation}
                selector={createSelector(idx, selector)}
              />,
            ],
          }),
          {} as Record<
            string,
            React.ReactElement<{
              selector: string;
            }>[]
          >
        )
      );
    }
  }, [instance, nodeRef, attrs, selector]);

  return (
    // TODO remove attrs until we have a better way to handle them from SelectionPanel
    <fcomposition
      ref={nodeRef}
      id={id}
      {...restProps}
      attrs={attrs}
      instance={instance}
      selector={selector}
      shallowParent={(restProps as any).parent}
      visible={restProps.visible !== false && layers.isEnabled(FShallowNodeTypesEnum.FComposition)}
    >
      {Object.entries(nodes).map(([type, children], idx) => {
        const groupSelector = createSelector(idx, selector);
        return <FGroup children={children} key={type} selector={groupSelector} />;
      })}
    </fcomposition>
  );
};
